import { memberDuplicateQueryOptions } from '@/api/functionsQueryOptions'
import {
    MembershipRequestType,
    membershipRequestSchema,
} from '@/api/membershipRequest'
import { useCreateMembershipRequestMutation } from '@/api/membershipRequestQueryOptions'
import { getMember, getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import {
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/createMembershipRequest_/$membershipUuid'
)({
    component: CreateMembershipRequestComponent,
})

function CreateMembershipRequestComponent() {
    const params = Route.useParams()

    const duplicateQuery = useSuspenseQuery(
        memberDuplicateQueryOptions(params.membershipUuid),
    )
    const duplicate = duplicateQuery.data

    const navigate = useNavigate()
    const onSuccess = (newMembershipRequest: MembershipRequestType) => {
        newMembershipRequest
        enqueueSnackbar('Anfrage gestellt', { variant: 'success' })
        navigate({
            to: '/memberships',
            search: {
                filter: [],
                sort: [],
                visibility: [{ field: 'properties.joblist' }],
            },
        })
    }

    const onError = (error: any) => {
        error
        enqueueSnackbar('Fehler beim Speichern der Mitgliedschaftsanfrage', {
            variant: 'error',
        })
    }
    const sessionOrganization = useSelector(getOrganization)
    const organizationUuid = sessionOrganization.uuid

    const sessionMember = useSelector(getMember)
    const memberUuid = sessionMember.uuid

    const membershipRequest = {
        scoutstate: 'MEMBER_FULL',
        message: '',
        member: { uuid: duplicate.member.uuid },
        mainOrganization: { uuid: duplicate.organization.uuid },
        requestingOrganization: { uuid: organizationUuid },
        requestingUser: { uuid: memberUuid },
    } as MembershipRequestType

    const { mutate: createMembershipRequest } =
        useCreateMembershipRequestMutation(organizationUuid, onSuccess, onError)

    const {
        control,
        register,
        handleSubmit,
        formState: { isDirty, isValid },
    } = useForm<MembershipRequestType>({
        resolver: yupResolver<MembershipRequestType, any, any>(membershipRequestSchema),
        mode: 'onChange',
        shouldFocusError: true,
    })

    const onSubmit = (data: MembershipRequestType) => {
        console.log('onSubmit')
        if (isValid) {
            const newMembershipRequest = {
                requestingOrganization: { uuid: organizationUuid },
                requestingUser: { uuid: memberUuid },
                mainOrganization: { uuid: duplicate.organization.uuid },
                member: { uuid: duplicate.member.uuid },
                scoutstate: data.scoutstate,
                message: data.message,
            } as MembershipRequestType
            console.log(JSON.stringify(newMembershipRequest))
            createMembershipRequest(newMembershipRequest)
        }
    }

    return (<>
        <h1>
            {t('entity.membershipRequest')} {t('property.membershipRequest.for')}
        </h1>
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1}>
                <Grid
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 8
                    }}>
                    <div>
                        <Grid container spacing={1}>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <input
                                    type="hidden"
                                    {...register('uuid')}
                                    defaultValue="new"
                                />
                                <input
                                    type="hidden"
                                    {...register('member.uuid')}
                                    defaultValue={duplicate.member.uuid}
                                />
                                <input
                                    type="hidden"
                                    {...register('mainOrganization.uuid')}
                                    defaultValue={duplicate.organization.uuid}
                                />
                                <input
                                    type="hidden"
                                    {...register('requestingOrganization.uuid')}
                                    defaultValue={organizationUuid}
                                />
                                <input
                                    type="hidden"
                                    {...register('requestingUser.uuid')}
                                    defaultValue={memberUuid}
                                />
                                <TextField
                                    {...register('member.scoutId')}
                                    defaultValue={duplicate.member.scoutId}
                                    disabled={true}
                                    label="Scout-ID"
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <TextField
                                    {...register('member.lastname')}
                                    defaultValue={duplicate.member.lastname}
                                    disabled={true}
                                    label={t('property.member.lastname')}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <TextField
                                    {...register('member.firstname')}
                                    defaultValue={duplicate.member.firstname}
                                    disabled={true}
                                    label={t('property.member.firstname')}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 6
                    }}>
                    <FormControl fullWidth variant="standard">
                        <InputLabel id="job-select-label">
                            {t('property.membership.scoutstate')}
                        </InputLabel>
                        <Controller
                            name="scoutstate"
                            control={control}
                            defaultValue={membershipRequest.scoutstate}
                            render={({ field }) => (
                                <Select labelId="job-select-label" {...field}>
                                    <MenuItem value="MEMBER_FULL">
                                        {t('enum.scoutstate.MEMBER_FULL')}
                                    </MenuItem>
                                    <MenuItem value="MEMBER_ADD">
                                        {t('enum.scoutstate.MEMBER_ADD')}
                                    </MenuItem>
                                    <MenuItem value="CONTACT">
                                        {t('enum.scoutstate.CONTACT')}
                                    </MenuItem>
                                </Select>
                            )}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 6
                    }}>
                    <TextField
                        {...register('message')}
                        label={t('property.membershipRequest.message')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    <Button type="submit" disabled={!isDirty}>
                        {t('action.send')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    </>);
}
